import type { FC, ReactNode, RefObject } from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeSlashIcon } from '@heroicons/react/24/outline';
import { QuotedPostPreview } from '@shared/dream-components';
import cx from 'classnames';

import { ImpressionSuppressionContext } from '~/context/ImpressionSuppressionContext';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { CommunityPost } from '~/interfaces/community';
import { CommunitySendableType } from '~/interfaces/community/community';
import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';
import { isResourceDiscussionHidden } from '~/routes/community/_utils/isResourceDiscussionHidden';
import { isDuplicateEmbedTitle } from '~/routes/community/_utils/postEmbedDedupe';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { EditedIndicator } from '../ui/EditedIndicator';

import { CommentSection } from '../../posts/$id/_components/comments/CommentSection';
import { PodcastEpisodeEmbedCard } from '../PodcastEpisodeEmbedCard';
import { PostActionMenu } from '../PostActionMenu';
import { PostBody } from '../PostBody';
import { PostEmbedCard } from '../PostEmbedCard';
import { PostLink } from '../PostLink';
import { PostAuthorHeader } from './_components/PostAuthorHeader';
import { PostCardActions } from './_components/PostCardActions';
import { PostCardCounts } from './_components/PostCardCounts';
import { GatedPostCard } from './GatedPostCard';

interface PostCardProps {
  post: CommunityPost;
  resourceContent?: ReactNode;
  readOnly?: boolean;
  hideSpace?: boolean;
  // Set only where a profile pin is meaningful — the author's profile Posts tab. The space
  // feed must leave this off, or the pin marker would follow the post everywhere.
  isProfilePinned?: boolean;
  // Impression surface; omit to opt the card out of impression tracking.
  pageBlock?: NectarPageBlock;
}

export const PostCard: FC<PostCardProps> = ({
  post,
  hideSpace = false,
  isProfilePinned = false,
  pageBlock,
  resourceContent,
  readOnly = false,
}) => {
  const isGated = post.gated === true;
  const isLoggedIn = useIsLoggedIn();

  // Posts behind a gated overlay are mounted but blurred/inert, so don't count
  // an impression for content the viewer can't actually read. A gated card counts
  // the unlock gate over the post, not a read of the post, and only for logged-in
  // members (guests get no gate). It doesn't wait for the tier catalog, so it can
  // fire a moment before the CTA paints.
  const suppressed = useContext(ImpressionSuppressionContext);
  const impressionRef = useNectarImpression(
    {
      sendableId: post.id,
      sendableType: CommunitySendableType.CommunityPost,
      pageBlock: isGated ? NectarPageBlock.CommunityFeedGatedPosts : pageBlock,
      communitySpaceId: post.space?.id,
    },
    { fireWhenInViewport: true, enabled: !!pageBlock && !suppressed && (!isGated || isLoggedIn) },
  );

  const postProps = {
    ref: impressionRef,
    post,
    hideSpace,
    isProfilePinned,
    resourceContent,
    readOnly,
  } as const;

  return isGated ? <GatedPostCard {...postProps} /> : <PostCardContent {...postProps} />;
};

interface PostCardContentProps extends Omit<PostCardProps, 'pageBlock'> {
  ref: RefObject<HTMLElement | null>;
}

// Ungated card.
const PostCardContent: FC<PostCardContentProps> = ({
  ref,
  post,
  hideSpace = false,
  isProfilePinned = false,
  resourceContent,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const titleId = `post-title-${post.id}`;
  const hideTitle = !!resourceContent || isDuplicateEmbedTitle(post);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
  const [optimisticCommentCount, setOptimisticCommentCount] = useState(0);
  const commentsCount = Math.max(0, post.comments_count + optimisticCommentCount);
  const isBanned = post.author.computed?.banned === true;
  const isDiscussionHidden = isResourceDiscussionHidden(post);

  return (
    <article
      ref={ref}
      className={cx('border rounded-3xl w-full', {
        'border-red-300': isBanned,
        'border-black/10': !isBanned,
        'opacity-60': isBanned || post.hidden_from_members,
      })}
      aria-labelledby={hideTitle ? undefined : titleId}
      // aria-labelledby wins while the visible title renders; this names the article when the duplicated title is hidden
      aria-label={post.title}
    >
      {isBanned && (
        <div className="flex items-center gap-1.5 px-4 pt-3 text-xs text-red-500">
          <EyeSlashIcon className="size-3.5" />
          <span>{t('community.banned_post_notice')}</span>
        </div>
      )}

      <div className="flex items-center gap-6 pt-4 px-4">
        <PostAuthorHeader
          author={post.author}
          createdAt={post.created_at}
          space={post.space}
          hideSpace={hideSpace}
          isPinned={isProfilePinned || (hideSpace && post.is_pinned)}
        />
        {!readOnly && <PostActionMenu post={post} triggerVariant="default" className="shrink-0" alignmentOffset={6} />}
      </div>

      <div className="flex flex-col gap-4 px-4 pt-4 pb-4">
        {!hideTitle && (
          <PostLink postId={post.id}>
            <h3 id={titleId} className="text-xl font-semibold leading-6 tracking-[-0.4px] text-black break-words">
              {post.title}
            </h3>
          </PostLink>
        )}

        {resourceContent}

        <PostBody tiptapState={post.tiptap_state} postId={post.id} clampText />

        {post.attachment && (
          <div className="border border-black/10 rounded-2xl p-4 flex flex-col gap-3 overflow-clip">
            {post.attachment.imageUrl && (
              <div className="aspect-[724/421] w-full overflow-hidden rounded">
                <img
                  src={communityImageSrc(post.attachment.imageUrl, 1300)}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {post.attachment.title && (
              <p className="text-xl font-semibold leading-6 tracking-[-0.4px] text-black">{post.attachment.title}</p>
            )}
            {post.attachment.excerpt && (
              <p className="text-base leading-6 text-black overflow-hidden text-ellipsis">{post.attachment.excerpt}</p>
            )}
          </div>
        )}

        {post.attachable?.type === 'post' && <PostEmbedCard postId={post.id} attachable={post.attachable} />}
        {post.attachable?.type === 'episode' && <PodcastEpisodeEmbedCard attachable={post.attachable} />}
        {post.attachable?.type === 'quoted_post' && (
          <PostLink postId={post.attachable.id} className="block rounded-2xl transition-colors hover:bg-black/[0.02]">
            <QuotedPostPreview post={post.attachable} />
          </PostLink>
        )}

        {post.edited_at && <EditedIndicator editedAt={post.edited_at} />}
      </div>

      {readOnly ? (
        <PostCardCounts post={post} />
      ) : (
        <PostCardActions
          post={post}
          commentsCount={commentsCount}
          isCommentsExpanded={isCommentsExpanded}
          onToggleComments={() => setIsCommentsExpanded((previousValue) => !previousValue)}
        />
      )}
      {!readOnly && !isDiscussionHidden && (isCommentsExpanded || post.comments_preview) && (
        <div className="border-t border-black/10 px-2">
          <CommentSection
            postId={post.id}
            spaceSlug={post.space?.slug}
            commentsState={post.comments_state}
            preview={post.comments_preview ? { comment: post.comments_preview, commentsCount } : undefined}
            showFullThread={isCommentsExpanded}
            onShowFullThread={() => setIsCommentsExpanded(true)}
            onCommentCreated={() => setOptimisticCommentCount((prev) => prev + 1)}
            onCommentDeleted={() => setOptimisticCommentCount((prev) => prev - 1)}
            onDeleteRollback={() => setOptimisticCommentCount((prev) => prev + 1)}
          />
        </div>
      )}
    </article>
  );
};
